import * as React from 'react';

import { useDispatch } from 'react-redux';
import { FlexySelect } from '@europrocurement/flexy-components';
import { DefFrequenceApiObject } from '@europrocurement/l2d-domain/reducers/achats/slices/defFrequenceSlice';
import { AchatsAppDispatch, useAchatsService } from '@europrocurement/l2d-domain';

import { ErrorState, FrequencySelectorProps } from './types';
import { createErrorComponents } from './createErrorComponents';

/**
 * Frequency Selector for updating entities with frequencies
 */
const FrequencySelector: React.FunctionComponent<FrequencySelectorProps> = function (props) {
    const {
        frequencyDatasource,
        fetchFrequencies,
        placeholder = 'Sélectionner une fréquence',
        variant,
    } = props;

    const { getDefFrequence } = useAchatsService();

    const dispatch = useDispatch<AchatsAppDispatch>();
    const [errorState, setErrorState] = React.useState<ErrorState>({
        hasError: false,
        message: '',
    });

    // Créer les composants d'erreur une seule fois quand le message d'erreur change
    const errorComponents = React.useMemo(
        () => createErrorComponents(errorState.message),
        [errorState.message],
    );

    React.useEffect(() => {
        if (frequencyDatasource.data.length === 0) {
            const fetchMethod = fetchFrequencies ?? (() => dispatch(getDefFrequence({})));
            fetchMethod();
        }
    }, [dispatch, frequencyDatasource.data.length, fetchFrequencies, getDefFrequence]);

    // Find selected frequency, determine if selector is enabled, and check for errors
    const { selectedFrequency, isDisabled, entityCode, entityFrequencyId } = React.useMemo(() => {
        // Initialize with error state
        const result = {
            selectedFrequency: null,
            isDisabled: true,
            entityCode: '',
            entityFrequencyId: 0,
        };

        try {
            if (variant === 'codeRejet') {
                const { codeRejet } = props;

                // Check if code is defined
                if (!codeRejet.code) {
                    const errorMessage = 'Code de rejet non défini';
                    console.error('FrequencySelector Error:', errorMessage);
                    setErrorState({
                        hasError: true,
                        message: 'Code de rejet manquant',
                        details: errorMessage,
                    });
                    return result;
                }

                // Clear error state if no issues
                setErrorState({ hasError: false, message: '' });

                const selectedFrequencyLogic =
                    frequencyDatasource.data.length > 0
                        ? frequencyDatasource.data.find(
                              (f) => f.dfrId === codeRejet.idFrequenceRelance,
                          ) || frequencyDatasource.data[0]
                        : null;

                return {
                    entityCode: codeRejet.code,
                    entityFrequencyId: codeRejet.idFrequenceRelance,
                    isDisabled: !codeRejet.relanceAuto,
                    selectedFrequency: selectedFrequencyLogic,
                };
            }
            const { exceptionFacturante } = props;

            // Check if code is defined
            if (!exceptionFacturante.id) {
                const errorMessage = "Code d'exception facturante non défini";
                console.error('FrequencySelector Error:', errorMessage);
                setErrorState({
                    hasError: true,
                    message: "Code d'exception manquant",
                    details: errorMessage,
                });
                return result;
            }

            // Clear error state if no issues
            setErrorState({ hasError: false, message: '' });

            return {
                entityCode: exceptionFacturante.id,
                entityFrequencyId: exceptionFacturante.idFrequenceRelance,
                isDisabled: !exceptionFacturante.relanceAuto,
                selectedFrequency:
                    frequencyDatasource.data.length > 0
                        ? frequencyDatasource.data.find(
                              (f) => f.dfrId === exceptionFacturante.idFrequenceRelance,
                          ) || frequencyDatasource.data[0]
                        : null,
            };
        } catch (error) {
            // Catch any unexpected errors
            const errorMessage = error instanceof Error ? error.message : 'Erreur inconnue';
            console.error('FrequencySelector Error:', errorMessage);
            setErrorState({
                hasError: true,
                message: 'Erreur interne',
                details: errorMessage,
            });
            return result;
        }
    }, [frequencyDatasource.data, props, variant]);

    const formatFrequencyLibelle = (dfrLibelle?: string | null) => {
        if (!dfrLibelle) return ' - ';

        if (dfrLibelle === 'Indéfini') {
            return 'Jamais';
        }

        return dfrLibelle;
    };

    // Prepare options
    const options = React.useMemo(
        () =>
            frequencyDatasource.data.map((frequency) => ({
                label: formatFrequencyLibelle(frequency.dfrLibelle),
                value: frequency.dfrId,
                data: frequency,
            })),
        [frequencyDatasource.data],
    );

    // Handle frequency change
    const handleFrequencyChange = (selectedOption: {
        label: string;
        value: number;
        data: DefFrequenceApiObject;
    }) => {
        if (isDisabled || errorState.hasError) {
            return;
        }

        try {
            if (variant === 'codeRejet') {
                const { patchCodeRejet, fetchCodeRejetData } = props;
                patchCodeRejet(entityCode.toString(), { idFrequenceRelance: selectedOption.value });
                setTimeout(() => {
                    dispatch(fetchCodeRejetData({}));
                }, 500);
            } else {
                const { patchExceptionFacturante, fetchExceptionFacturanteData } = props;
                patchExceptionFacturante(entityCode.toString(), {
                    idFrequenceRelance: selectedOption.value,
                });
                setTimeout(() => {
                    dispatch(fetchExceptionFacturanteData({}));
                }, 500);
            }
        } catch (error) {
            const errorMessage = error instanceof Error ? error.message : 'Erreur inconnue';
            console.error('FrequencySelector Change Error:', errorMessage);
            setErrorState({
                hasError: true,
                message: 'Erreur lors de la mise à jour',
                details: errorMessage,
            });
        }
    };

    // Si en état d'erreur, afficher l'indicateur d'erreur
    if (errorState.hasError) {
        return (
            <FlexySelect
                components={errorComponents}
                inputValue=""
                value={null}
                options={[]}
                isDisabled
                error
                helperText={errorState.message}
            />
        );
    }

    // If no frequency data is loaded yet
    if (!selectedFrequency) return null;

    return (
        <FlexySelect
            placeholder={placeholder}
            onChange={handleFrequencyChange}
            inputValue={
                selectedFrequency.dfrLibelle && !isDisabled
                    ? formatFrequencyLibelle(selectedFrequency.dfrLibelle)
                    : ' - '
            }
            value={entityFrequencyId}
            options={options}
            isDisabled={isDisabled}
            filterOption={() => true}
            tabSelectsValue={false}
            backspaceRemovesValue={false}
            noOptionsMessage={() => 'Aucune fréquence sélectionnée'}
        />
    );
};

export default FrequencySelector;
